import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type LibraryCategory = Tables<"library_categories">;
export type LibraryItemCategoryAssignment = Tables<"library_item_categories">;
export type LibraryCategoryColorKey = (typeof libraryCategoryPalette)[number]["value"];

export const libraryCategoryPalette = [
  { value: "silver", label: "Silver", toneClassName: "library-category-tone-silver" },
  { value: "indigo", label: "Indigo", toneClassName: "library-category-tone-indigo" },
  { value: "sage", label: "Sage", toneClassName: "library-category-tone-sage" },
  { value: "amber", label: "Amber", toneClassName: "library-category-tone-amber" },
  { value: "coral", label: "Coral", toneClassName: "library-category-tone-coral" },
  { value: "sky", label: "Sky", toneClassName: "library-category-tone-sky" },
  { value: "plum", label: "Plum", toneClassName: "library-category-tone-plum" },
] as const;

const paletteValues = new Set<string>(libraryCategoryPalette.map((entry) => entry.value));

const normalizeName = (value: string) => value.trim().replace(/\s+/g, " ");

const getFriendlyMessage = (error: { code?: string; message?: string } | null, fallback: string) => {
  if (error?.code === "23505") return "That category name already exists in this workspace.";
  return error?.message ?? fallback;
};

export const getLibraryCategoryToneClassName = (color: string | null | undefined) => {
  const normalized = color && paletteValues.has(color) ? color : "indigo";
  return libraryCategoryPalette.find((entry) => entry.value === normalized)?.toneClassName ?? "library-category-tone-indigo";
};

export const fetchLibraryCategories = async (workspaceId: string) => {
  const { data, error } = await supabase
    .from("library_categories")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("name", { ascending: true });

  if (error) throw new Error(getFriendlyMessage(error, "Failed to load library categories"));
  return (data ?? []) as LibraryCategory[];
};

export const fetchLibraryItemCategoryAssignments = async (itemIds: string[]) => {
  if (!itemIds.length) return [] as LibraryItemCategoryAssignment[];

  const { data, error } = await supabase
    .from("library_item_categories")
    .select("library_item_id, library_category_id, created_at")
    .in("library_item_id", itemIds);

  if (error) throw new Error(getFriendlyMessage(error, "Failed to load library category assignments"));
  return (data ?? []) as LibraryItemCategoryAssignment[];
};

export const createLibraryCategory = async (
  workspaceId: string,
  values: { name: string; color: LibraryCategoryColorKey },
) => {
  const payload: TablesInsert<"library_categories"> = {
    workspace_id: workspaceId,
    name: normalizeName(values.name),
    color: paletteValues.has(values.color) ? values.color : "indigo",
  };

  if (!payload.name) throw new Error("Category name is required.");

  const { data, error } = await supabase
    .from("library_categories")
    .insert(payload)
    .select("*")
    .single();

  if (error) throw new Error(getFriendlyMessage(error, "Failed to create library category"));
  return data as LibraryCategory;
};

export const updateLibraryCategory = async (
  categoryId: string,
  values: Partial<Pick<TablesUpdate<"library_categories">, "name" | "color">>,
) => {
  const payload: TablesUpdate<"library_categories"> = {};

  if (typeof values.name === "string") {
    payload.name = normalizeName(values.name);
    if (!payload.name) throw new Error("Category name is required.");
  }

  if (typeof values.color === "string") {
    payload.color = paletteValues.has(values.color) ? values.color : "indigo";
  }

  const { data, error } = await supabase
    .from("library_categories")
    .update(payload)
    .eq("id", categoryId)
    .select("*")
    .single();

  if (error) throw new Error(getFriendlyMessage(error, "Failed to update library category"));
  return data as LibraryCategory;
};

export const deleteLibraryCategory = async (categoryId: string) => {
  const { error } = await supabase.from("library_categories").delete().eq("id", categoryId);
  if (error) throw new Error(getFriendlyMessage(error, "Failed to delete library category"));
};

export const fetchLibraryCategoryUsageCounts = async (
  workspaceId: string,
): Promise<Record<string, number>> => {
  // Join through library_items to scope by workspace
  const { data, error } = await supabase
    .from("library_item_categories")
    .select("library_category_id, library_items!inner(workspace_id)")
    .eq("library_items.workspace_id", workspaceId);

  if (error) throw new Error(getFriendlyMessage(error, "Failed to load category usage counts"));
  const counts: Record<string, number> = {};
  for (const row of (data ?? []) as Array<{ library_category_id: string }>) {
    counts[row.library_category_id] = (counts[row.library_category_id] ?? 0) + 1;
  }
  return counts;
};

export const mergeLibraryCategory = async (
  sourceCategoryId: string,
  targetCategoryId: string,
) => {
  if (sourceCategoryId === targetCategoryId) {
    throw new Error("Choose a different category to merge into.");
  }

  // Fetch all source assignments
  const { data: sourceLinks, error: srcErr } = await supabase
    .from("library_item_categories")
    .select("library_item_id")
    .eq("library_category_id", sourceCategoryId);
  if (srcErr) throw new Error(getFriendlyMessage(srcErr, "Failed to merge category"));

  // Fetch existing target assignments to dedup
  const sourceItemIds = (sourceLinks ?? []).map((r) => r.library_item_id);
  const targetItemIds = new Set<string>();
  if (sourceItemIds.length) {
    const { data: targetLinks, error: tgtErr } = await supabase
      .from("library_item_categories")
      .select("library_item_id")
      .eq("library_category_id", targetCategoryId)
      .in("library_item_id", sourceItemIds);
    if (tgtErr) throw new Error(getFriendlyMessage(tgtErr, "Failed to merge category"));
    for (const row of targetLinks ?? []) targetItemIds.add(row.library_item_id);
  }

  const toInsert: TablesInsert<"library_item_categories">[] = sourceItemIds
    .filter((id) => !targetItemIds.has(id))
    .map((library_item_id) => ({
      library_item_id,
      library_category_id: targetCategoryId,
    }));

  if (toInsert.length) {
    const { error: insErr } = await supabase.from("library_item_categories").insert(toInsert);
    if (insErr) throw new Error(getFriendlyMessage(insErr, "Failed to merge category"));
  }

  // Remove all source links
  const { error: delLinkErr } = await supabase
    .from("library_item_categories")
    .delete()
    .eq("library_category_id", sourceCategoryId);
  if (delLinkErr) throw new Error(getFriendlyMessage(delLinkErr, "Failed to merge category"));

  // Delete source category
  const { error: delErr } = await supabase
    .from("library_categories")
    .delete()
    .eq("id", sourceCategoryId);
  if (delErr) throw new Error(getFriendlyMessage(delErr, "Failed to merge category"));
};

export const setWorkspaceDefaultLibraryCategory = async (workspaceId: string, categoryId: string) => {
  const { error } = await supabase
    .from("profiles")
    .update({ default_library_category_id: categoryId })
    .eq("id", workspaceId);

  if (error) throw new Error(getFriendlyMessage(error, "Failed to set the default library category"));
};

export const replaceLibraryItemCategories = async (libraryItemId: string, categoryIds: string[]) => {
  const uniqueCategoryIds = Array.from(new Set(categoryIds));

  const { error: deleteError } = await supabase
    .from("library_item_categories")
    .delete()
    .eq("library_item_id", libraryItemId);

  if (deleteError) throw new Error(getFriendlyMessage(deleteError, "Failed to update library item categories"));

  if (!uniqueCategoryIds.length) return [] as LibraryItemCategoryAssignment[];

  const payload: TablesInsert<"library_item_categories">[] = uniqueCategoryIds.map((libraryCategoryId) => ({
    library_item_id: libraryItemId,
    library_category_id: libraryCategoryId,
  }));

  const { data, error } = await supabase
    .from("library_item_categories")
    .insert(payload)
    .select("library_item_id, library_category_id, created_at");

  if (error) throw new Error(getFriendlyMessage(error, "Failed to update library item categories"));
  return (data ?? []) as LibraryItemCategoryAssignment[];
};

export type LibraryItemWithCategories<T extends { id: string }> = T & {
  categories: LibraryCategory[];
};

export const hydrateLibraryItemsWithCategories = <T extends { id: string }>(
  items: T[],
  categories: LibraryCategory[],
  assignments: LibraryItemCategoryAssignment[],
): LibraryItemWithCategories<T>[] => {
  const categoryMap = new Map(categories.map((category) => [category.id, category]));
  const itemMap = new Map<string, LibraryCategory[]>();

  assignments.forEach((assignment) => {
    const category = categoryMap.get(assignment.library_category_id);
    if (!category) return;
    itemMap.set(assignment.library_item_id, [...(itemMap.get(assignment.library_item_id) ?? []), category]);
  });

  return items.map((item) => ({
    ...item,
    categories: (itemMap.get(item.id) ?? []).sort((a, b) => a.name.localeCompare(b.name)),
  }));
};
